// Thin wrappers around Bobbin's localhost HTTP API.
//
// The Conversation Viewer reads conversation/turn data from Thread cloud
// (Supabase via /api/conversations) but the audio + speaker-label-edit
// affordances live on the user's own Mac, served by Bobbin at
// 127.0.0.1:8770. Both kinds of call coexist in one viewer:
//
//   - Cloud reads → fetch('/api/conversations/...') via the same origin
//   - Local writes/audio → fetch('http://127.0.0.1:8770/...')
//
// CORS preflight is OK because Bobbin's sidecar uses CorsLayer::permissive().
// If Bobbin isn't running (or the user isn't on the Mac with the data),
// the calls fail with a network error and the UI surfaces a banner.

const BOBBIN_BASE = "http://127.0.0.1:8770";

export class BobbinUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BobbinUnavailableError";
  }
}

async function bobbinFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    const resp = await fetch(`${BOBBIN_BASE}${path}`, init);
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`${init?.method ?? "GET"} ${path}: ${resp.status} ${resp.statusText} ${text}`);
    }
    return resp;
  } catch (e) {
    if (e instanceof TypeError) {
      // fetch throws TypeError on network failure (CORS, connection refused).
      throw new BobbinUnavailableError(
        `Can't reach Bobbin at ${BOBBIN_BASE}. Is the sidecar running?`,
      );
    }
    throw e;
  }
}

/**
 * Stream a meeting utterance's audio. Returns a blob URL the caller can
 * assign to an <audio src=...> element. Caller is responsible for revoking
 * the URL when done (URL.revokeObjectURL).
 *
 * `sourceUri` is the utterance's source_uri (e.g.
 * `meeting://2026-05-20_1202#utterance_42`). Bobbin resolves it to the
 * parent meeting + serves the right source file based on the
 * utterance's `metadata.source_track`.
 */
export async function fetchUtteranceAudioBlobUrl(
  sourceUri: string,
  track?: string | null,
): Promise<string> {
  const params = new URLSearchParams({ source_uri: sourceUri });
  if (track) params.set("track", track);
  const resp = await bobbinFetch(`/api/meetings/audio-by-source-uri?${params}`);
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

export interface PatchSpeakerResult {
  id: string;
  speaker: string | null;
  speaker_resolved: string | null;
  speaker_person_id: string | null;
  source_updated_at: string | null;
}

/**
 * Update an utterance's resolved speaker label. Pass `null` to clear the
 * resolution and fall back to the original track-name speaker.
 */
export async function patchUtteranceSpeaker(
  sourceUri: string,
  speakerLabel: string | null,
  speakerPersonId?: string | null,
): Promise<PatchSpeakerResult> {
  const resp = await bobbinFetch(
    "/api/meetings/utterances/by-source-uri/speaker",
    {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        source_uri: sourceUri,
        speaker_label: speakerLabel,
        speaker_person_id: speakerPersonId ?? null,
      }),
    },
  );
  return (await resp.json()) as PatchSpeakerResult;
}
